/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */

import React, { useEffect, useRef } from 'react';
import {
  Engine,
  Scene,
  EngineOptions,
  SceneOptions,
  ArcRotateCamera,
} from '@babylonjs/core';

// This import will inject interseptrs to  fetch,  xhr, etc, to remove cache buster
// but must be activated before anythig is setup here (and so it is advisable to only do it at index.tsx)
// import './babylon-chachebuster-interseptor';
// This one is to activate logs to uunderstand what babylon is  requestion (does not show cache buster even if they will go out)
// import './babylon-io-logging';  // Use the same import at top level index.tsx to make sure it really injects the logs

type BabylonComponentSceneParameters = {

  // Engine options
  antiAlias : boolean;
  adaptToDeviceRatio : boolean,
  engineOptions : EngineOptions,

  // Scene options
  sceneOptions : SceneOptions

  // Callbacks
  callbackOnSceneReady : (scene : Scene, invalidateRender: () => void) => void
};

export function BabylonComponentScene(
  {
    antiAlias,
    engineOptions,
    adaptToDeviceRatio,
    sceneOptions,
    callbackOnSceneReady,
  } : BabylonComponentSceneParameters,
) : JSX.Element {

  const reactCanvas = useRef<HTMLCanvasElement | null>(null);

  // Keep stable references to avoid multiple creations under StrictMode or re-renders
  const engineRef = useRef<Engine | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const renderRequestedRef = useRef(true);

  const invalidateRender = () => {
    renderRequestedRef.current = true;
  };

  useEffect(() => {
    const canvas = reactCanvas.current;
    if (!canvas) return;

    // Guard: if an engine already exists (e.g., StrictMode mount/unmount cycle), do nothing
    if (engineRef.current) return;

    // Create the engine
    const engine = new Engine(canvas, antiAlias, engineOptions, adaptToDeviceRatio);
    engineRef.current = engine;

    // Create the scene
    const scene = new Scene(engine, sceneOptions);
    sceneRef.current = scene;

    // expose for debugging
    (window as any).engine = engine;
    (window as any).scene = scene;

    // Enable collisions in the scene, for the camera movement
    scene.collisionsEnabled = true;

    let isPointerDown = false;

    const onPointerDown = () => {
      isPointerDown = true;
      invalidateRender();
    };

    const onPointerMove = () => {
      if (isPointerDown) {
        invalidateRender();
      }
    };

    const onPointerUp = () => {
      isPointerDown = false;
      invalidateRender();
    };

    const onWheel = () => {
      invalidateRender();
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('wheel', onWheel);

    // Notify when ready
    if (scene.isReady()) {
      callbackOnSceneReady(scene, invalidateRender);
    } else {
      scene.onReadyObservable.addOnce(() => {
        callbackOnSceneReady(scene, invalidateRender);
      });
    }

    // -----------------------------------------------------------
    // Render loop with manual invalidation and fps limit to avoid
    // unnecessary renders when the scene is static (which is most
    // of the time in our use case, i hope) and with this we will
    // stop hanging all other browser activities when the scene is
    // heavy and the user is not interacting with it.
    // -----------------------------------------------------------
    const targetFps = 30;
    const frameIntervalMs = 1000 / targetFps;

    let lastRenderTime = 0;

    const renderLoop = () => {
      if (document.hidden) {
        return;
      }

      if (!renderRequestedRef.current) {
        return;
      }

      const now = performance.now();

      if (now - lastRenderTime < frameIntervalMs) {
        return;
      }

      lastRenderTime = now;

      renderRequestedRef.current = false;
      scene.render();

      const activeCamera = scene.activeCamera as ArcRotateCamera | null;
      if (
        activeCamera
        && (
          Math.abs(activeCamera.inertialAlphaOffset) > 0.0001
          || Math.abs(activeCamera.inertialBetaOffset) > 0.0001
          || Math.abs(activeCamera.inertialRadiusOffset) > 0.0001
          || Math.abs(activeCamera.inertialPanningX) > 0.0001
          || Math.abs(activeCamera.inertialPanningY) > 0.0001
        )
      ) {
        renderRequestedRef.current = true;
      }
    };

    engine.runRenderLoop(renderLoop);

    // Handle resizes
    const onResize = () => {
      engine.resize();
      invalidateRender();
    };
    window.addEventListener('resize', onResize);

    // Cleanup: stop loop, remove listeners, dispose engine (which i hope disposes scenes/resources aad all that..you know)
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', onResize);

      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);

      try {
        engine.stopRenderLoop(renderLoop);
      } catch {
        // ignore
      }

      try {
        engine.dispose(); // disposes scenes, textures, buffers, etc.
      } finally {
        engineRef.current = null;
        sceneRef.current = null;
        (window as any).engine = undefined;
        (window as any).scene = undefined;
      }
    };

    // Intentionally empty deps: create once per mount.
    // If we want to recreate when options change, add them to deps and this cleanup will safely dispose the old engine.
  }, []); // do not depend on reactCanvas; its ref object identity is stable

  return (
    <canvas
      ref={reactCanvas}
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 0,
        objectFit: 'cover',
      }}
    />
  );
}
